import AppLayout from "@/components/app-layout";
import { usePageTitle } from "@/hooks/use-page-title";
import { ViewOnlyBanner } from "@/hooks/use-permissions";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ArrowLeft, MapPin, Home, TrendingUp, DollarSign, Building, Calendar, Ruler, Landmark, Percent, FileText, Clock, Loader2, Calculator } from "lucide-react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { useLocation, useParams, Link } from "wouter";
import type { BuyDeal } from "@shared/schema";
import { ListingPhotoGallery } from "@/components/listing-photo-gallery";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

function toNum(val: string | number | null | undefined): number {
  if (val == null) return 0;
  return typeof val === "string" ? parseFloat(val) : val;
}

function formatCurrency(amount: number | string | null | undefined): string {
  const num = toNum(amount);
  if (num === 0) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(num);
}

function formatPercent(val: number | string | null | undefined): string {
  const num = toNum(val);
  if (num === 0) return "—";
  return `${num}%`;
}

function StatCard({ label, value, icon: Icon, color = "text-gray-900" }: { label: string; value: string; icon?: any; color?: string }) {
  return (
    <div className="bg-white border border-gray-100 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon className="h-4 w-4 text-gray-400" />}
        <span className="text-xs text-gray-500 uppercase tracking-wide">{label}</span>
      </div>
      <p className={`text-lg font-bold ${color}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>{value}</p>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string | number | null | undefined }) {
  if (value == null || value === "" || value === 0 || value === "0") return null;
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-medium text-gray-900">{value}</span>
    </div>
  );
}

export default function AppBuyDetail() {
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const params = useParams<{ id: string }>();
  const dealId = parseInt(params.id || "0", 10);

  const { toast } = useToast();
  const { data: deal, isLoading } = useQuery<BuyDeal>({
    queryKey: ["/api/app/buy-deals", dealId],
    enabled: dealId > 0,
  });
  usePageTitle(deal?.address || "Deal");

  const { data: copyCheck } = useQuery<{ propertyId: number | null }>({
    queryKey: ["/api/app/underwriting/copy-check", "buydeal", dealId],
    queryFn: async () => {
      const res = await fetch(`/api/app/underwriting/copy-check?sourceType=buydeal&sourceListingId=${dealId}`, { credentials: "include" });
      return res.json();
    },
    enabled: dealId > 0,
  });
  const alreadyCopied = copyCheck?.propertyId != null;

  const copyMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/underwriting/copy-from-listing", { sourceType: "buydeal", sourceListingId: dealId });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/underwriting/copy-check", "buydeal", dealId] });
      navigate(`/app/underwriting/${data.propertyId}`);
    },
    onError: () => toast({ title: "Could not copy to underwriting", variant: "destructive" }),
  });

  if (isLoading) {
    return (
      <AppLayout>
        <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-64 w-full rounded-xl" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!deal) {
    return (
      <AppLayout>
        <div className="max-w-5xl mx-auto px-4 py-12 text-center">
          <Home className="h-16 w-16 mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Deal not found</h2>
          <p className="text-gray-500 mb-4">This deal may have been removed or is no longer available.</p>
          <Button asChild data-testid="button-back-to-deals">
            <Link href="/app/buy"><ArrowLeft className="h-4 w-4 mr-2" /> Back to Deals</Link>
          </Button>
        </div>
      </AppLayout>
    );
  }

  const coc = toNum(deal.cashOnCash);
  const cocColor = coc >= 20 ? "text-green-600" : coc >= 17 ? "text-blue-600" : "text-amber-600";
  const dealPhotos = ((deal.photos ?? []).filter(Boolean) as string[]);
  const galleryPhotos = dealPhotos.length > 0 ? dealPhotos : (deal.imageUrl ? [deal.imageUrl] : []);

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto px-4 py-6">
        <ViewOnlyBanner area="properties" />
        <Link
          href="/app/buy"
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 mb-6 transition-colors"
          data-testid="button-back-to-deals"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Deals
        </Link>

        <div className="mb-6">
          <ListingPhotoGallery
            photos={galleryPhotos}
            testIdPrefix={`deal-detail-${deal.id}`}
            alt={deal.address}
            heroHeight="h-72 md:h-96"
            accent="blue"
            rounded
            thumbnailContainerClassName="pt-3"
            overlay={
              <>
                {deal.isSample && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span className="text-white/30 text-7xl md:text-8xl font-black uppercase tracking-[0.4em] rotate-[-20deg] select-none"
                      style={{ textShadow: "0 2px 12px rgba(0,0,0,0.12)" }}
                    >
                      SAMPLE
                    </span>
                  </div>
                )}
                <div className="absolute bottom-4 left-4 flex gap-2">
                  <Badge className="bg-white/90 text-gray-800 border-0 shadow-sm backdrop-blur-sm px-3 py-1.5">
                    <MapPin className="h-3.5 w-3.5 mr-1.5" />
                    {deal.market}
                  </Badge>
                  {deal.isSample && (
                    <Badge className="bg-amber-500/90 text-white border-0 shadow-sm backdrop-blur-sm px-3 py-1.5">
                      <Clock className="h-3.5 w-3.5 mr-1.5" />
                      Sample Listing
                    </Badge>
                  )}
                </div>
              </>
            }
          />
        </div>

        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1" data-testid="text-deal-address" data-tour="buy-detail-heading">
              {deal.address}
            </h1>
            <p className="text-gray-500">
              {deal.city}, {deal.state} {deal.zipCode || ""}
              {deal.provider && <span className="ml-2 text-gray-400">· via {deal.provider}</span>}
            </p>
          </div>
          <Button
            variant={alreadyCopied ? "default" : "outline"}
            onClick={() => {
              if (alreadyCopied && copyCheck?.propertyId) {
                navigate(`/app/underwriting/${copyCheck.propertyId}`);
              } else {
                copyMutation.mutate();
              }
            }}
            disabled={copyMutation.isPending}
            data-testid="button-copy-to-underwriting"
            data-tour="buy-detail-underwrite"
          >
            {copyMutation.isPending ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Copying...</>
            ) : alreadyCopied ? (
              <><Calculator className="h-4 w-4 mr-2" />Review in Underwriting</>
            ) : (
              <><Calculator className="h-4 w-4 mr-2" />Copy to Underwriting</>
            )}
          </Button>
          <div className="flex items-center gap-2">
            <ExplainerVideoButton pageKey="buy-detail" />
            <GuideButton pageKey="buy-detail" />
          </div>
        </div>
        <GuideNudgeBanner pageKey="buy-detail" />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard label="Purchase Price" value={formatCurrency(deal.purchasePrice)} icon={DollarSign} />
          <StatCard label="Cash-on-Cash" value={formatPercent(deal.cashOnCash)} icon={TrendingUp} color={cocColor} />
          <StatCard label="Monthly Cash Flow" value={formatCurrency(deal.monthlyCashFlow)} icon={DollarSign} color={cocColor} />
          <StatCard label="Cap Rate" value={formatPercent(deal.capRate)} icon={Percent} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white border border-gray-100 rounded-xl p-5" data-tour="buy-detail-details">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Building className="h-4 w-4 text-gray-400" />
              Property Details
            </h3>
            <DetailRow label="Property Type" value={deal.propertyType} />
            <DetailRow label="Current Bedrooms" value={deal.currentBedrooms} />
            <DetailRow label="Current Bathrooms" value={deal.currentBathrooms} />
            {deal.garageSpaces != null && deal.garageSpaces > 0 && <DetailRow label="Garage Spaces" value={deal.garageSpaces} />}
            {deal.futureBedrooms && <DetailRow label="Future Bedrooms (PadSplit)" value={deal.futureBedrooms} />}
            {deal.futureBathrooms && <DetailRow label="Future Bathrooms (PadSplit)" value={deal.futureBathrooms} />}
            {deal.sqft && <DetailRow label="Square Footage" value={`${deal.sqft.toLocaleString()} sqft`} />}
            {deal.lotSize && <DetailRow label="Lot Size" value={`${toNum(deal.lotSize)} acres`} />}
            {deal.yearBuilt && <DetailRow label="Year Built" value={deal.yearBuilt} />}
            <DetailRow label="MLS #" value={deal.mlsNumber} />
          </div>

          <div className="bg-white border border-gray-100 rounded-xl p-5" data-tour="buy-detail-financials">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-gray-400" />
              Financial Summary
            </h3>
            <DetailRow label="Purchase Price" value={formatCurrency(deal.purchasePrice)} />
            <DetailRow label="ARV (After Repair Value)" value={formatCurrency(deal.arv)} />
            <DetailRow label="Rehab Cost" value={formatCurrency(deal.rehabCost)} />
            <DetailRow label="Monthly Rent" value={formatCurrency(deal.monthlyRent)} />
            <DetailRow label="Monthly Expenses" value={formatCurrency(deal.monthlyExpenses)} />
            <DetailRow label="NOI (Annual)" value={formatCurrency(deal.noi)} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white border border-gray-100 rounded-xl p-5">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Landmark className="h-4 w-4 text-gray-400" />
              Financing
            </h3>
            <DetailRow label="Down Payment" value={formatCurrency(deal.downPayment)} />
            <DetailRow label="Loan Amount" value={formatCurrency(deal.loanAmount)} />
            <DetailRow label="Interest Rate" value={formatPercent(deal.interestRate)} />
            {deal.loanTermYears && <DetailRow label="Loan Term" value={`${deal.loanTermYears} years`} />}
            <DetailRow label="Monthly Mortgage" value={formatCurrency(deal.monthlyMortgage)} />
          </div>

          <div className="bg-white border border-gray-100 rounded-xl p-5">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Ruler className="h-4 w-4 text-gray-400" />
              Operating Costs
            </h3>
            <DetailRow label="Insurance (Monthly)" value={formatCurrency(deal.insuranceCost)} />
            <DetailRow label="Taxes (Annual)" value={formatCurrency(deal.taxesAnnual)} />
            <DetailRow label="HOA (Monthly)" value={formatCurrency(deal.hoaMonthly)} />
            <DetailRow label="Vacancy Rate" value={formatPercent(deal.vacancyRate)} />
            <DetailRow label="PadSplit Fee (Commission + Booking)" value={formatPercent(deal.managementFee)} />
          </div>
        </div>

        {deal.notes && (
          <div className="bg-white border border-gray-100 rounded-xl p-5 mb-8">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <FileText className="h-4 w-4 text-gray-400" />
              Notes
            </h3>
            <p className="text-sm text-gray-600 leading-relaxed" data-testid="text-deal-notes">{deal.notes}</p>
          </div>
        )}

        <div className="border-t border-gray-100 pt-4 mb-8">
          <p className="text-xs text-gray-400 leading-relaxed" data-testid="text-disclaimer">
            * All financial projections, property details, and investment metrics displayed on this page are provided by third-party sources and are intended for informational purposes only. PadPulse makes no representations or warranties regarding the accuracy, completeness, or reliability of this information. These figures should not be relied upon as the sole basis for any investment decision. Prospective investors should conduct their own independent due diligence and consult with qualified financial, legal, and real estate professionals before making any investment. Past performance and projected returns are not guarantees of future results.
          </p>
        </div>
      </div>
    </AppLayout>
  );
}
